import { attackerWinProbability } from './battle';
import { CARD_SUPPORT_CAP, cardSupportLogOdds } from './cardBattle';
import { cardLogit, cardLogistic, cardPairModel } from './cardPower';
import { pieceById } from './movement';
import { rollCurve, rollWinProbability } from './rollBattle';
import type { GameContext, GameState } from './types';

/**
 * The attacker's chance as the board AI reads it: the odds the game's own combat mode settles the
 * fight by. Public prior only: board search must never inspect opponents' hidden hands.
 */
export function strategicWinProbability(ctx: GameContext, state: GameState): number {
  if (ctx.combatMode === 'roll') return rollOdds(ctx, state);
  if (ctx.combatMode !== 'card') return attackerWinProbability(ctx, state);
  const pending = state.pendingBattle;
  if (!pending) return 0;
  const a = pieceById(state, pending.attackerId),
    d = pieceById(state, pending.defenderId);
  if (!a || !d) return 0;
  const odds = cardLogit(cardPairModel(ctx, state, a, d).probability);
  let score = 0;
  for (let mine = 0; mine <= CARD_SUPPORT_CAP; mine++)
    for (let theirs = 0; theirs <= CARD_SUPPORT_CAP; theirs++) {
      score += cardLogistic(odds + cardSupportLogOdds(ctx) * (mine - theirs));
    }
  return score / (CARD_SUPPORT_CAP + 1) ** 2;
}

/**
 * Roll settles a fight at the seats' own levels (`rollWinProbability`), so the AI reads the same
 * odds. Between two sides at one level the factor is nothing, and that level's odds (its own table
 * where the game has one) are read on the level's Roll curve (phase D).
 */
function rollOdds(ctx: GameContext, state: GameState): number {
  const pending = state.pendingBattle;
  const a = pending && pieceById(state, pending.attackerId);
  const d = pending && pieceById(state, pending.defenderId);
  const level = (faction: string | undefined) => (faction && ctx.cardLevels?.[faction]) || 'expert';
  if (!a || !d) return attackerWinProbability(ctx, state, ctx.cardLevels ?? {});
  if (level(a.faction) === level(d.faction))
    return rollCurve(
      ctx,
      attackerWinProbability(ctx, state, ctx.cardLevels ?? {}),
      level(a.faction),
    );
  return rollWinProbability(ctx, state, { levels: ctx.cardLevels });
}
